import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    MessageSquare, Plus, RefreshCw, Trash2, Pencil, CheckCircle2, AlertCircle,
    Clock, Layers, Building2, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
    listIceBreakers, deleteIceBreaker, resyncIceBreaker,
    type IceBreakerRow,
} from '@/services/api/ice-breakers';
import IceBreakerForm from './components/IceBreakerForm';

export default function IceBreakers() {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const [editing, setEditing] = useState<IceBreakerRow | null>(null);
    const [formOpen, setFormOpen] = useState(false);

    const { data: items = [], isLoading } = useQuery({
        queryKey: ['ice-breakers'],
        queryFn: listIceBreakers,
        refetchInterval: 1000 * 30,
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => deleteIceBreaker(id),
        onSuccess: () => {
            toast({ title: 'Removido', description: 'Ice breaker removido (e desconfigurado no Messenger).' });
            queryClient.invalidateQueries({ queryKey: ['ice-breakers'] });
        },
        onError: (e: any) => {
            toast({ title: 'Erro', description: e?.response?.data?.detail || 'Falha ao remover', variant: 'destructive' });
        },
    });

    const resyncMutation = useMutation({
        mutationFn: (id: number) => resyncIceBreaker(id),
        onSuccess: () => {
            toast({ title: 'Re-sincronizado', description: 'Aplicado novamente nas páginas.' });
            queryClient.invalidateQueries({ queryKey: ['ice-breakers'] });
        },
        onError: (e: any) => {
            toast({ title: 'Erro', description: e?.response?.data?.detail || 'Falha no sync', variant: 'destructive' });
        },
    });

    const handleEdit = (item: IceBreakerRow) => {
        setEditing(item);
        setFormOpen(true);
    };

    const handleCreate = () => {
        setEditing(null);
        setFormOpen(true);
    };

    const handleDelete = (id: number) => {
        if (confirm('Remover esse ice breaker? Isso vai desconfigurar do Messenger nas páginas afetadas.')) {
            deleteMutation.mutate(id);
        }
    };

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-2xl p-6 text-white flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="bg-white/10 p-3 rounded-xl">
                        <MessageSquare className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Quebra-gelo (Ice Breakers)</h1>
                        <p className="text-sm text-white/60">
                            Mensagens recomendadas que aparecem na primeira interação com a página
                        </p>
                    </div>
                </div>
                <Button
                    onClick={handleCreate}
                    className="bg-white text-[#0f172a] hover:bg-white/90 font-medium"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Nova configuração
                </Button>
            </div>

            {/* Info */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-900">
                    <p className="font-medium mb-1">Como funciona</p>
                    <ul className="list-disc list-inside space-y-0.5 text-amber-800 text-xs">
                        <li><strong>Por categoria</strong>: aplica nas páginas vinculadas a ela (e novas que entrarem)</li>
                        <li><strong>Por página</strong>: sobrepõe a config da categoria (override individual)</li>
                        <li>Até <strong>4 perguntas</strong> por configuração (limite do Meta)</li>
                        <li>Pra cada pergunta: pode disparar um <strong>flow existente</strong> ou um <strong>postback custom</strong></li>
                    </ul>
                </div>
            </div>

            {/* Lista */}
            {isLoading ? (
                <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-400 text-sm">
                    Carregando...
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                    <MessageSquare className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm font-medium text-gray-600 mb-1">Nenhum quebra-gelo configurado</p>
                    <p className="text-xs text-gray-400 mb-4">
                        Suas páginas estão usando apenas o botão padrão "Começar".
                    </p>
                    <Button onClick={handleCreate} size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Criar primeira configuração
                    </Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map(item => (
                        <IceBreakerCard
                            key={item.id}
                            item={item}
                            onEdit={() => handleEdit(item)}
                            onDelete={() => handleDelete(item.id)}
                            onResync={() => resyncMutation.mutate(item.id)}
                            resyncLoading={resyncMutation.isPending && resyncMutation.variables === item.id}
                        />
                    ))}
                </div>
            )}

            {formOpen && (
                <IceBreakerForm
                    initial={editing}
                    onClose={() => { setFormOpen(false); setEditing(null); }}
                    onSaved={() => {
                        setFormOpen(false);
                        setEditing(null);
                        queryClient.invalidateQueries({ queryKey: ['ice-breakers'] });
                    }}
                />
            )}
        </div>
    );
}

function IceBreakerCard({
    item, onEdit, onDelete, onResync, resyncLoading,
}: {
    item: IceBreakerRow;
    onEdit: () => void;
    onDelete: () => void;
    onResync: () => void;
    resyncLoading: boolean;
}) {
    const isCategory = item.scope === 'category';
    const Icon = isCategory ? Layers : Building2;

    const statusBadge =
        item.sync_status === 'synced' ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-50 text-emerald-700">
                <CheckCircle2 className="w-3 h-3" />
                Sincronizado
            </span>
        ) : item.sync_status === 'partial' ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-amber-50 text-amber-700" title={item.sync_error || ''}>
                <AlertCircle className="w-3 h-3" />
                Sync parcial
            </span>
        ) : item.sync_status === 'error' ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-red-50 text-red-700" title={item.sync_error || ''}>
                <AlertCircle className="w-3 h-3" />
                Erro no sync
            </span>
        ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-700">
                <Clock className="w-3 h-3" />
                Pendente
            </span>
        );

    return (
        <div className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`p-2 rounded-lg shrink-0 ${isCategory ? 'bg-violet-50' : 'bg-blue-50'}`}>
                        <Icon className={`w-5 h-5 ${isCategory ? 'text-violet-600' : 'text-blue-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${isCategory ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>
                                {isCategory ? 'Categoria' : 'Página'}
                            </span>
                            <h3 className="font-semibold text-gray-800 truncate">
                                {isCategory ? item.category_name : item.page_name}
                            </h3>
                            <span className="text-xs text-gray-400">
                                · {item.affected_pages_count} {item.affected_pages_count === 1 ? 'página' : 'páginas'}
                            </span>
                            {statusBadge}
                            {!item.is_active && (
                                <span className="text-[11px] text-gray-500 italic">(desativado)</span>
                            )}
                        </div>

                        <div className="space-y-1 mt-3">
                            {item.questions.slice(0, 4).map((q, i) => (
                                <div key={i} className="text-xs text-gray-700 flex items-center gap-2 pl-1">
                                    <span className="text-gray-400">{i + 1}.</span>
                                    <span className="font-medium truncate flex-1" title={q.question}>{q.question}</span>
                                    {q.action_type === 'flow' && q.flow_id && (
                                        <span className="text-[10px] text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">
                                            → Flow #{q.flow_id}
                                        </span>
                                    )}
                                    {q.action_type === 'postback' && (
                                        <span className="text-[10px] text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded font-mono truncate max-w-[120px]" title={q.payload}>
                                            {q.payload}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>

                        {item.last_synced_at && (
                            <p className="text-[10px] text-gray-400 mt-3">
                                Último sync: {new Date(item.last_synced_at).toLocaleString('pt-BR')}
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                    <button
                        onClick={onResync}
                        disabled={resyncLoading}
                        title="Re-sincronizar com Meta"
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${resyncLoading ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                        onClick={onEdit}
                        title="Editar"
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <Pencil className="w-4 h-4" />
                    </button>
                    <button
                        onClick={onDelete}
                        title="Remover"
                        className="p-2 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}
